import React, { useState } from 'react';
import { Button, Input, Form, Space, Typography, Alert } from 'antd';
import { ScanOutlined, EditOutlined } from '@ant-design/icons';
import { useAppDispatch } from '../../../redux/hooks';
import { setVehicle } from '../../../redux/reducers/vehicleSlice';
import Api from '../../../services/api';
import { BarcodeScanner } from '../../../components/BarcodeScanner';

const { Title, Text } = Typography;

interface VinScanStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const VinScanStep: React.FC<VinScanStepProps> = ({ onNext }) => {
  const [mode, setMode] = useState<'scan' | 'manual'>('scan');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const handleScan = () => {
    setShowScanner(true);
  };

  const handleBarcodeScan = async (barcode: string) => {
    console.log('Scanned barcode:', barcode);
    
    // Close scanner first
    setShowScanner(false);
    
    // Small delay to ensure modal closes properly
    setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        // The barcode might be a VIN or vehicle number
        const vehicle = await Api.getVehicle(barcode);

        if (vehicle) {
          dispatch(setVehicle(vehicle));
          onNext();
        }
      } catch (err: any) {
        console.error('Vehicle lookup error:', err);
        
        // Check if it's a 404 (vehicle not found) vs 401 (unauthorized)
        if (err?.statusCode === 404) {
          setError('Vehicle not found. Please try again or enter manually.');
        } else if (err?.statusCode === 401) {
          setError('Authentication issue. Please try again.');
        } else {
          const errorMessage = err?.message || 'Failed to lookup vehicle from barcode';
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    }, 100);
  };

  const handleManualSubmit = async (values: Record<string, any>) => {
    setLoading(true);
    setError(null);

    try {
      const vehicleNumber = values.vehicleNumber || values.vin;
      const vehicle = await Api.getVehicle(vehicleNumber);

      if (vehicle) {
        dispatch(setVehicle(vehicle));
        onNext();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to lookup vehicle';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="step-container">
      <Title level={3}>Identify Vehicle</Title>
      <Text type="secondary">Scan the vehicle&apos;s barcode or enter details manually</Text>

      <BarcodeScanner
        visible={showScanner}
        onScan={handleBarcodeScan}
        onClose={() => setShowScanner(false)}
        title="Scan Vehicle Barcode"
      />

      {mode === 'scan' ? (
        <div className="scan-container">
          <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 40 }}>
            <Button 
              type="primary" 
              size="large" 
              icon={<ScanOutlined />}
              onClick={handleScan}
              loading={loading}
              disabled={loading}
              style={{ width: '100%', height: 60 }}
            >
              {loading ? 'Processing...' : 'Scan Vehicle Barcode'}
            </Button>
            
            <Button 
              size="large" 
              icon={<EditOutlined />}
              onClick={() => setMode('manual')}
              disabled={loading}
              style={{ width: '100%' }}
            >
              Enter Manually
            </Button>
            
          </Space>

          {error && (
            <Alert 
              message={error} 
              type="warning" 
              showIcon 
              style={{ marginTop: 20 }}
            />
          )}
        </div>
      ) : (
        <div className="manual-entry-container">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleManualSubmit}
            style={{ marginTop: 20 }}
          >
            <Form.Item
              label="Vehicle Number"
              name="vehicleNumber"
              rules={[
                { required: true, message: 'Please enter vehicle number or VIN' },
              ]}
            >
              <Input 
                size="large" 
                placeholder="Enter vehicle number"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              label="VIN (Optional)"
              name="vin"
              rules={[
                { len: 17, message: 'VIN must be 17 characters' },
              ]}
            >
              <Input 
                size="large" 
                placeholder="Enter 17-character VIN"
                maxLength={17}
                disabled={loading}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>

            {error && (
              <Alert 
                message={error} 
                type="error" 
                showIcon 
                style={{ marginBottom: 20 }}
              />
            )}

            <div className="step-actions">
              <Button onClick={() => setMode('scan')} disabled={loading}>
                Back to Scan
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Looking up...' : 'Look Up Vehicle'}
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default VinScanStep;